"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguageNavigate } from "@/lib/hooks/useLanguageNavigate";
import { useTikTokActions } from "@/lib/hooks/useTikTok";
import { toast } from "@/components/ui/use-toast";
import { Loader2, ShieldAlert, AlertCircle, Music } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

function isPermissionError(err: unknown): boolean {
  const code = (err as any)?.code;
  if (code === "permission_denied") return true;
  const msg = ((err as any)?.message || String(err)).toLowerCase();
  return (
    msg.includes("access_denied") ||
    msg.includes("permission") ||
    msg.includes("scope") ||
    msg.includes("denied") ||
    msg.includes("not authorized") ||
    msg.includes("authorization") ||
    msg.includes("insufficient")
  );
}

export default function TikTokCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useLanguageNavigate();
  const tiktok = useTikTokActions();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [isPermission, setIsPermission] = useState(false);

  const REQUIRED_SCOPES = ["user.info.basic", "video.upload", "video.publish"];

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");
    // TikTok sends granted scopes as "scopes" (plural) in the callback URL
    // Fall back to "scope" if not present (defensive)
    const scopeParam = searchParams.get("scopes") || searchParams.get("scope");

    if (errorParam) {
      const checkStr = errorParam + " " + (searchParams.get("error_description") || "");
      const rawErr = { message: checkStr };
      const permErr = isPermissionError(rawErr);
      setIsPermission(permErr);
      if (permErr) {
        setError(t("tiktok.insufficientPermissions"));
      } else {
        setError(searchParams.get("error_description") || t("tiktok.oauthFailed"));
        setTimeout(() => navigate("/account"), 4000);
      }
      return;
    }

    // Check if all required scopes were granted
    if (scopeParam) {
      const granted = scopeParam.split(",").map((s) => s.trim());
      const missing = REQUIRED_SCOPES.filter((s) => !granted.includes(s));
      if (missing.length > 0) {
        setIsPermission(true);
        setError(t("tiktok.insufficientPermissions"));
        return;
      }
    }

    if (!code || !state) {
      setError(t("tiktok.invalidCallback"));
      setTimeout(() => navigate("/account"), 3000);
      return;
    }

    tiktok
      .handleCallback(code, state)
      .then((result) => {
        toast({
          title: t("tiktok.connected"),
          description: t("tiktok.connectedDesc", { username: result.tiktokUsername }),
        });
        navigate("/account");
      })
      .catch((err) => {
        const permErr = isPermissionError(err);
        setIsPermission(permErr);
        if (permErr) {
          setError(t("tiktok.insufficientPermissions"));
        } else {
          setError((err as Error).message || t("tiktok.oauthFailed"));
          setTimeout(() => navigate("/account"), 4000);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = async () => {
    setError(null);
    setIsPermission(false);
    try {
      const { authUrl } = await tiktok.getAuthUrl();
      window.location.href = authUrl;
    } catch {
      navigate("/account");
    }
  };

  return (
    <div className="min-h-screen bg-lavender flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-8 max-w-md w-full flex flex-col items-center gap-6 text-center">
        {error ? (
          isPermission ? (
            <>
              <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200">
                <ShieldAlert className="w-10 h-10 text-amber-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold">{t("tiktok.permissionDeniedTitle")}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{error}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <Button
                  onClick={handleRetry}
                  className="flex-1 bg-black hover:bg-black/90 text-white rounded-lg h-11"
                >
                  <Music className="w-4 h-4 mr-2" />
                  {t("tiktok.tryAgain")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/account")}
                  className="flex-1 rounded-lg h-11"
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-2xl bg-red-50 border border-red-200">
                <AlertCircle className="w-10 h-10 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-bold text-destructive">{t("tiktok.oauthFailed")}</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
              <p className="text-xs text-muted-foreground">{t("tiktok.redirecting")}</p>
            </>
          )
        ) : (
          <>
            <div className="p-4 rounded-2xl bg-zinc-100 border border-zinc-200">
              <Loader2 className="w-10 h-10 animate-spin text-zinc-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-bold">{t("tiktok.connectAccount")}</h2>
              <p className="text-sm text-muted-foreground">{t("tiktok.connecting")}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
